import { dataService } from '../apis/dataService';
import type { 
  FinancialDataQueryParams,
  CashflowStatement,
  IncomeStatement,
  BalanceSheet,
  Unit,
  Currency
} from '../../types/api';
import type { ChartSeries } from '../../types/charts';
import { convertUSDToCurrency } from './currencyConversion';

/**
 * Fetch data for Overview Combined Charts
 */

async function fetchAllUnitsData<T>(
  user_id_no: number,
  clientUnits: Array<{ unit_id_no?: number }>,
  fetchFn: (params: FinancialDataQueryParams) => Promise<T[]>
): Promise<Array<T & { unit_id_no: number }>> {
  const promises = clientUnits.map(async (unit) => {
    if (!unit.unit_id_no) return null;
    const params: FinancialDataQueryParams = { user_id_no, unit_id_no: unit.unit_id_no };
    try {
      const data = await fetchFn(params);
      // Attach unit_id_no to each item so we can look up its currency during conversion
      return data.map(item => ({ ...item, unit_id_no: unit.unit_id_no! }));
    } catch (error) {
      console.warn(`Failed to fetch data for unit ${unit.unit_id_no}:`, error);
      return null;
    }
  });

  const results = await Promise.all(promises);
  return results.filter((r): r is Array<T & { unit_id_no: number }> => r !== null).flat();
}

/**
 * Convert value from unit's currency to target currency
 */
function convertToTargetCurrency(
  value: number,
  unitId: number,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): number {
  if (!targetCurrency) return value;
  
  const unit = allUnits.find(u => u.unit_id_no === unitId);
  const sourceCurrency = unit?.currency || 'USD';
  
  if (sourceCurrency === targetCurrency) return value;
  
  // Logic: Unit Currency -> USD -> Target Currency
  // 1. Get rate for Unit Currency
  const sourceRateObj = currencies.find(c => c.currency === sourceCurrency);
  const sourceUnitsPerUsd = sourceRateObj?.units_per_usd || 1;
  const valueInUsd = value / sourceUnitsPerUsd;
  
  // 2. Convert USD to Target
  return convertUSDToCurrency(valueInUsd, targetCurrency, currencies);
}

/**
 * Normalizes dates to MMM YYYY for display
 */
const formatDate = (dateStr: string): string => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const normalized = dateStr.includes('T') ? dateStr.slice(0, 10) : dateStr;
  if (normalized.length === 6 && /^\d{6}$/.test(normalized)) {
    const year = normalized.substring(2, 4);
    const month = parseInt(normalized.substring(4, 6), 10);
    return `${monthNames[month - 1]} '${year}`;
  }
  if (normalized.length >= 10 && normalized.includes('-')) {
    const [year, month] = normalized.split('-');
    const yearShort = year.substring(2, 4);
    return `${monthNames[parseInt(month, 10) - 1]} '${yearShort}`;
  }
  return dateStr;
};

const sortDataPoints = (data: Array<{ x: string; y: number }>) => {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return data.sort((a, b) => {
    const parse = (str: string) => {
      const parts = str.split(" '");
      if (parts.length < 2) return 0;
      const m = parts[0];
      const y = parseInt(parts[1]);
      return (y < 50 ? 2000 + y : 1900 + y) * 12 + monthNames.indexOf(m);
    };
    return parse(a.x) - parse(b.x);
  });
};

/**
 * 1. Portfolio Value
 * Area: Total Portfolio Value (Unit Value [8])
 * Line: Net Equity (Equity Valuation [13])
 */
export async function fetchPortfolioValueData(
  user_id_no: number,
  clientUnits: Array<{ unit_id_no?: number }>,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): Promise<ChartSeries[]> {
  const rawData = await fetchAllUnitsData(user_id_no, clientUnits, dataService.getBalanceSheet);
  
  const unitValueMap = new Map<string, number>();
  const equityValuationMap = new Map<string, number>();

  rawData.forEach(item => {
    const date = formatDate(item.calendar_date);
    // BalanceSheet does not have portioned_value, so we check type or use category_value
    const rawValue = (item as any).portioned_value ?? (item as any).category_value ?? 0;
    const value = convertToTargetCurrency(rawValue, item.unit_id_no, allUnits, targetCurrency, currencies);
    
    if (item.category_code === 8) {
      unitValueMap.set(date, (unitValueMap.get(date) || 0) + value);
    } else if (item.category_code === 13) {
      equityValuationMap.set(date, (equityValuationMap.get(date) || 0) + value);
    }
  });

  const dates = Array.from(new Set([...unitValueMap.keys(), ...equityValuationMap.keys()]));
  
  const unitValueSeries = sortDataPoints(dates.map(d => ({ x: d, y: unitValueMap.get(d) || 0 })));
  const equityValuationSeries = sortDataPoints(dates.map(d => ({ x: d, y: equityValuationMap.get(d) || 0 })));

  return [
    { label: 'Total Portfolio Value', data: unitValueSeries, color: 'primary', type: 'area' },
    { label: 'Net Equity', data: equityValuationSeries, color: 'success', type: 'line' }
  ];
}

/** Build the portfolio-value chart from an already-loaded overview response. */
export function buildPortfolioValueData(
  rawData: Array<BalanceSheet & { unit_id_no: number }>,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): ChartSeries[] {
  const unitValueMap = new Map<string, number>();
  const equityValuationMap = new Map<string, number>();
  rawData.forEach((item) => {
    const date = formatDate(item.calendar_date);
    const value = convertToTargetCurrency(
      (item as any).portioned_value ?? item.category_value ?? 0,
      item.unit_id_no,
      allUnits,
      targetCurrency,
      currencies,
    );
    if (item.category_code === 8) unitValueMap.set(date, (unitValueMap.get(date) || 0) + value);
    if (item.category_code === 13) equityValuationMap.set(date, (equityValuationMap.get(date) || 0) + value);
  });
  const dates = Array.from(new Set([...unitValueMap.keys(), ...equityValuationMap.keys()]));
  return [
    { label: 'Total Portfolio Value', data: sortDataPoints(dates.map((x) => ({ x, y: unitValueMap.get(x) || 0 }))), color: 'primary', type: 'area' },
    { label: 'Net Equity', data: sortDataPoints(dates.map((x) => ({ x, y: equityValuationMap.get(x) || 0 }))), color: 'success', type: 'line' },
  ];
}

/**
 * 2. Income vs Expenses
 * Area: Operating Expenses [4] (fallback: sum of first-level children)
 * Line: Gross / Rental Income [1] (fallback: Rental [2] + Other Income [3])
 */
const INCOME_PARENT_CODE = 1;
const INCOME_CHILD_CODES = new Set([2, 3]);
const EXPENSE_PARENT_CODE = 4;
const EXPENSE_CHILD_CODES = new Set([5, 9, 15, 20, 21]);

export async function fetchIncomeVsExpensesData(
  user_id_no: number,
  clientUnits: Array<{ unit_id_no?: number }>,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): Promise<ChartSeries[]> {
  const rawData = await fetchAllUnitsData(user_id_no, clientUnits, dataService.getIncomeStatement);
  
  const incomeParentMap = new Map<string, number>();
  const incomeChildMap = new Map<string, number>();
  const expenseParentMap = new Map<string, number>();
  const expenseChildMap = new Map<string, number>();

  rawData.forEach(item => {
    const date = formatDate(item.calendar_date);
    const rawValue = (item as any).portioned_value ?? (item as any).category_value ?? 0;
    const value = convertToTargetCurrency(rawValue, item.unit_id_no, allUnits, targetCurrency, currencies);
    
    if (item.category_code === INCOME_PARENT_CODE) {
      incomeParentMap.set(date, (incomeParentMap.get(date) || 0) + value);
    } else if (INCOME_CHILD_CODES.has(item.category_code)) {
      incomeChildMap.set(date, (incomeChildMap.get(date) || 0) + value);
    } else if (item.category_code === EXPENSE_PARENT_CODE) {
      expenseParentMap.set(date, (expenseParentMap.get(date) || 0) + value);
    } else if (EXPENSE_CHILD_CODES.has(item.category_code)) {
      expenseChildMap.set(date, (expenseChildMap.get(date) || 0) + value);
    }
  });

  const dates = Array.from(
    new Set([
      ...incomeParentMap.keys(),
      ...incomeChildMap.keys(),
      ...expenseParentMap.keys(),
      ...expenseChildMap.keys(),
    ]),
  );

  const incomeMap = new Map<string, number>();
  const expenseMap = new Map<string, number>();
  dates.forEach((date) => {
    const parentIncome = incomeParentMap.get(date);
    incomeMap.set(
      date,
      parentIncome !== undefined && parentIncome !== 0
        ? parentIncome
        : incomeChildMap.get(date) || 0,
    );
    const parentExpense = expenseParentMap.get(date);
    expenseMap.set(
      date,
      parentExpense !== undefined && parentExpense !== 0
        ? parentExpense
        : expenseChildMap.get(date) || 0,
    );
  });
  
  const expenseSeries = sortDataPoints(dates.map(d => ({ x: d, y: expenseMap.get(d) || 0 })));
  const incomeSeries = sortDataPoints(dates.map(d => ({ x: d, y: incomeMap.get(d) || 0 })));

  return [
    { label: 'Operating Expenses', data: expenseSeries, color: 'error', type: 'area' },
    { label: 'Rental Income', data: incomeSeries, color: 'success', type: 'line' }
  ];
}

/** Build income and expense series from cached TTM overview rows. */
export function buildIncomeVsExpensesData(
  rawData: Array<IncomeStatement & { unit_id_no: number }>,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): ChartSeries[] {
  const incomeParentMap = new Map<string, number>();
  const incomeChildMap = new Map<string, number>();
  const expenseParentMap = new Map<string, number>();
  const expenseChildMap = new Map<string, number>();
  rawData.forEach((item) => {
    const date = formatDate(item.calendar_date);
    const value = convertToTargetCurrency(
      (item as any).portioned_value ?? item.category_value ?? 0,
      item.unit_id_no,
      allUnits,
      targetCurrency,
      currencies,
    );
    if (item.category_code === INCOME_PARENT_CODE) {
      incomeParentMap.set(date, (incomeParentMap.get(date) || 0) + value);
    } else if (INCOME_CHILD_CODES.has(item.category_code)) {
      incomeChildMap.set(date, (incomeChildMap.get(date) || 0) + value);
    } else if (item.category_code === EXPENSE_PARENT_CODE) {
      expenseParentMap.set(date, (expenseParentMap.get(date) || 0) + value);
    } else if (EXPENSE_CHILD_CODES.has(item.category_code)) {
      expenseChildMap.set(date, (expenseChildMap.get(date) || 0) + value);
    }
  });
  const dates = Array.from(new Set([
    ...incomeParentMap.keys(),
    ...incomeChildMap.keys(),
    ...expenseParentMap.keys(),
    ...expenseChildMap.keys(),
  ]));
  const incomeMap = new Map<string, number>();
  const expenseMap = new Map<string, number>();
  dates.forEach((date) => {
    const parentIncome = incomeParentMap.get(date);
    incomeMap.set(
      date,
      parentIncome !== undefined && parentIncome !== 0 ? parentIncome : incomeChildMap.get(date) || 0,
    );
    const parentExpense = expenseParentMap.get(date);
    expenseMap.set(
      date,
      parentExpense !== undefined && parentExpense !== 0 ? parentExpense : expenseChildMap.get(date) || 0,
    );
  });
  return [
    { label: 'Operating Expenses', data: sortDataPoints(dates.map((x) => ({ x, y: expenseMap.get(x) || 0 }))), color: 'error', type: 'area' },
    { label: 'Rental Income', data: sortDataPoints(dates.map((x) => ({ x, y: incomeMap.get(x) || 0 }))), color: 'success', type: 'line' },
  ];
}

/**
 * 3. Net Cashflow
 * Area: Net Cash [30]
 */
export async function fetchNetCashflowCombinedData(
  user_id_no: number,
  clientUnits: Array<{ unit_id_no?: number }>,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): Promise<ChartSeries[]> {
  const rawData = await fetchAllUnitsData(user_id_no, clientUnits, dataService.getCashflow);
  
  const netCashMap = new Map<string, number>();

  rawData.forEach(item => {
    if (item.category_code === 30) {
      const date = formatDate(item.calendar_date);
      const rawValue = (item as any).portioned_value ?? (item as any).category_value ?? 0;
      const value = convertToTargetCurrency(rawValue, item.unit_id_no, allUnits, targetCurrency, currencies);
      netCashMap.set(date, (netCashMap.get(date) || 0) + value);
    }
  });

  const netCashSeries = sortDataPoints(Array.from(netCashMap.entries()).map(([x, y]) => ({ x, y })));

  return [
    { label: 'Net Cash', data: netCashSeries, color: 'info', type: 'area' }
  ];
}

/** Build the net-cash series from cached TTM overview rows. */
export function buildNetCashflowData(
  rawData: Array<CashflowStatement & { unit_id_no: number }>,
  allUnits: Unit[],
  targetCurrency: string,
  currencies: Currency[]
): ChartSeries[] {
  const netCash = new Map<string, number>();
  rawData.forEach((item) => {
    if (item.category_code !== 30) return;
    const date = formatDate(item.calendar_date);
    const value = convertToTargetCurrency(
      (item as any).portioned_value ?? item.category_value ?? 0,
      item.unit_id_no,
      allUnits,
      targetCurrency,
      currencies,
    );
    netCash.set(date, (netCash.get(date) || 0) + value);
  });
  return [{
    label: 'Net Cash',
    data: sortDataPoints(Array.from(netCash.entries()).map(([x, y]) => ({ x, y }))),
    color: 'info',
    type: 'area',
  }];
}


